import { create } from 'zustand'
import type { Settings } from '../types/data'
import { getUserSettings, saveUserSettings } from '../services/api'
import { getOrCreateUserUuid } from '../services/userManager'
import { 
  getAppSettings, 
  saveAppSettings 
} from '../services/promptManagerDB'
import { migrateAppSettingsFromLocalStorage } from '../services/dbManager'

interface SettingsState {
  settings: Settings
  loadSettings: () => Promise<void>
  saveSettings: () => Promise<void>
  updateSettings: (updates: Partial<Settings>, autoSave?: boolean) => void
}

const defaultSettings: Settings = {
  fontSize: 14,
  fontFamily: 'Microsoft YaHei, 微软雅黑, Arial, sans-serif',
  showBorder: true,
  mottoStyle: false,
  hideImageBorder: false,
  language: 'zh-CN'
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: defaultSettings,

  loadSettings: async () => {
    try {
      // 首先尝试从 localStorage 迁移到 InfiniteNote（一次性迁移）
      await migrateAppSettingsFromLocalStorage()

      // 优先从后端加载（如果有 userUuid）
      const userUuid = await getOrCreateUserUuid()
      if (userUuid) {
        try {
          const serverSettings = await getUserSettings(userUuid)
          if (serverSettings) {
            console.log('[SettingsStore] 从服务器加载设置:', serverSettings)
            set({ settings: { ...defaultSettings, ...serverSettings } })
            // 同时保存到 InfiniteNote 作为备份
            await saveAppSettings({ ...defaultSettings, ...serverSettings })
            return
          }
        } catch (error) {
          console.warn('[SettingsStore] 从服务器加载设置失败，使用本地设置:', error)
        }
      }

      // 从 InfiniteNote 加载（主存储）
      try {
        const saved = await getAppSettings()
        if (saved) {
          const parsed = typeof saved === 'string' ? JSON.parse(saved) : saved
          set({ settings: { ...defaultSettings, ...parsed } })
          return
        }
      } catch (e) {
        console.warn('[SettingsStore] 从 InfiniteNote 加载设置失败:', e)
      }

      // 如果所有加载都失败，使用默认设置
      console.log('[SettingsStore] 使用默认设置')
    } catch (error) {
      console.error('加载设置失败:', error)
    }
  },

  saveSettings: async () => {
    try {
      const settings = get().settings
      
      // 保存到 InfiniteNote（主存储）
      try {
        await saveAppSettings(settings)
        console.log('[SettingsStore] 设置已保存到 InfiniteNote 数据库')
      } catch (e) {
        console.error('[SettingsStore] 保存设置到 InfiniteNote 失败:', e)
        throw e // 如果 InfiniteNote 保存失败，抛出错误
      }
      
      // 保存到后端（如果有 userUuid）
      const userUuid = await getOrCreateUserUuid()
      if (userUuid) {
        try {
          const success = await saveUserSettings(userUuid, settings)
          if (success) {
            console.log('[SettingsStore] 设置已保存到服务器')
          } else {
            console.warn('[SettingsStore] 保存设置到服务器失败')
          }
        } catch (error) {
          console.warn('[SettingsStore] 保存设置到服务器失败:', error)
          // 服务器保存失败不影响本地保存，继续执行
        }
      }
    } catch (error) {
      console.error('保存设置失败:', error)
      // 不再降级到 localStorage，因为 IndexedDB 应该总是可用的
    }
  },

  updateSettings: (updates: Partial<Settings>, autoSave: boolean = true) => {
    set((state) => ({
      settings: { ...state.settings, ...updates }
    }))
    if (autoSave) {
      get().saveSettings()
    }
  }
}))
